import { Check, ChevronDown } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { projectPageSections } from '../../config/pages';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { PageId } from '../../types/navigation';

const projectSections = projectPageSections.filter(
  (section) => section.label !== 'Notifications' && section.items.length > 0
);

function ProjectPageNav() {
  const { activePage, selectNav } = useWorkspace();
  const menuRef = useRef<HTMLDetailsElement>(null);
  const currentPage = projectSections
    .flatMap((section) => section.items)
    .find((item) => item.id === activePage);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !menuRef.current?.contains(event.target)
      ) {
        if (menuRef.current) menuRef.current.open = false;
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, []);

  return (
    <nav className="scopePageNav" aria-label="Project navigation">
      <span className="scopePageNavContext">In this project</span>
      <details
        className="scopePageMenu"
        ref={menuRef}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && menuRef.current) {
            menuRef.current.open = false;
            menuRef.current.querySelector('summary')?.focus();
          }
        }}
      >
        <summary
          className="scopePageMenuTrigger"
          aria-label="Choose a project page"
        >
          <strong>{currentPage?.label ?? 'Project pages'}</strong>
          <ChevronDown size={15} aria-hidden="true" />
        </summary>
        <div className="scopePageMenuPanel">
          {projectSections.map((section) => (
            <section
              key={section.label}
              className="scopePageMenuGroup"
              aria-label={section.label}
            >
              <h2>{section.label}</h2>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = activePage === item.id;

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`scopePageMenuItem${active ? ' active' : ''}`}
                    onClick={() => {
                      selectNav(item.id as PageId);
                      if (menuRef.current) menuRef.current.open = false;
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon size={15} aria-hidden="true" />
                    <span>{item.label}</span>
                    {active ? <Check size={14} aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </section>
          ))}
        </div>
      </details>
    </nav>
  );
}

export { ProjectPageNav };
